import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useWelcomePage } from '../hooks/useWelcomePage';
import ReactMarkdown from 'react-markdown';
import { ROLES } from '@/constants';
import { formatDate } from '../lib/formatDate';
import { WelcomeTab } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`welcome-tabpanel-${index}`}
      aria-labelledby={`welcome-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Welcome: React.FC = () => {
  const { user } = useAuth();
  const { welcomePage, isLoading: loading, error, updateWelcomePage, isUpdating } = useWelcomePage();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState<{ tabs: WelcomeTab[] }>({ tabs: [] });
  const [activeTab, setActiveTab] = useState(0);

  const isAdmin = user?.role === ROLES.ADMIN;

  const handleEditClick = () => {
    if (welcomePage) {
      setEditForm({
        tabs: welcomePage.tabs,
      });
      setEditDialogOpen(true);
      setEditError('');
    }
  };

  const handleSave = async () => {
    try {
      setEditError('');
      updateWelcomePage(editForm);
      setEditDialogOpen(false);
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Failed to update welcome page');
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTabEdit = (index: number, field: 'title' | 'content', value: string) => {
    const newTabs = [...editForm.tabs];
    newTabs[index] = { ...newTabs[index], [field]: value };
    setEditForm({ tabs: newTabs });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{(error as any)?.message || 'Failed to load welcome page'}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome Page
          </Typography>
          {welcomePage && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {formatDate(welcomePage.updatedAt)}
              </Typography>
            </Box>
          )}
        </Box>
        
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            disabled={isUpdating}
          >
            Edit Content
          </Button>
        )}
      </Box>

      <Paper elevation={2}>
        {welcomePage?.tabs && welcomePage.tabs.length > 0 ? (
          <>
            {welcomePage.tabs.length > 1 ? (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="welcome tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                {welcomePage.tabs.map((tab, index) => (
                  <Tab key={tab.id} label={tab.title} id={`welcome-tab-${index}`} />
                ))}
              </Tabs>
            ) : null}
            {welcomePage.tabs.map((tab, index) => (
              <TabPanel key={tab.id} value={activeTab} index={index}>
                <Box sx={{ 
                  '& h1': { 
                    fontSize: '2rem', 
                    fontWeight: 'bold', 
                    mb: 2, 
                    mt: 0,
                    color: 'primary.main'
                  },
                  '& h2': { 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    mb: 1.5, 
                    mt: 3,
                    color: 'text.primary'
                  },
                  '& h3': { 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold', 
                    mb: 1, 
                    mt: 2,
                    color: 'text.primary'
                  },
                  '& p': { 
                    mb: 1.5, 
                    lineHeight: 1.6 
                  },
                  '& ul, & ol': { 
                    mb: 1.5, 
                    pl: 3 
                  },
                  '& li': { 
                    mb: 0.5, 
                    lineHeight: 1.6 
                  },
                  '& strong': { 
                    fontWeight: 'bold' 
                  },
                  '& em': { 
                    fontStyle: 'italic' 
                  },
                  '& blockquote': { 
                    borderLeft: '4px solid #1976d2', 
                    pl: 2, 
                    ml: 0, 
                    my: 2,
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  },
                  '& hr': { 
                    my: 3, 
                    borderColor: 'divider' 
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }
                }}>
                  <ReactMarkdown
                    components={{
                      a: ({ href, children, ...props }) => {
                        const isExternal = href?.startsWith('http://') || href?.startsWith('https://') || href?.startsWith('//');
                        // Filter out incompatible props (like ref) that Material-UI Link doesn't accept
                        const { ref, ...linkProps } = props as any;
                        return (
                          <Link
                            href={href || '#'}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            {...linkProps}
                          >
                            {children}
                          </Link>
                        );
                      },
                    }}
                  >
                    {tab.content}
                  </ReactMarkdown>
                </Box>
              </TabPanel>
            ))}
          </>
        ) : (
          <Box p={4}>
            <Typography variant="body1" color="text.secondary">
              No content available. {isAdmin && 'Click "Edit Content" to add welcome information.'}
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Welcome Page Content</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          {editForm.tabs.map((tab, index) => (
            <Box key={tab.id} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label={`Tab ${index + 1} Title`}
                value={tab.title}
                onChange={(e) => handleTabEdit(index, 'title', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={10}
                label={`Tab ${index + 1} Content`}
                value={tab.content}
                onChange={(e) => handleTabEdit(index, 'content', e.target.value)}
                placeholder="Enter markdown content..."
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Welcome;
